
/**
 * Monitors the loading progress of media elements (images and videos) 
 * within the given container, specifically focusing on "above the fold" content.
 */

let percentageRafId = null;

export function updatePageLoaderProgress(percentage) {
    const loaderBar = document.querySelector('.page-loader__progress >div');
    const percentageEl = document.querySelector('.page-loader__percentage');

    if (loaderBar) {
        loaderBar.style.width = `${percentage}%`;
    }

    if (percentageEl) {
        const target = Math.round(percentage);
        const current = parseInt(percentageEl.textContent) || 0;

        if (percentageRafId) {
            cancelAnimationFrame(percentageRafId);
        }

        const duration = 200; 
        const startTime = performance.now();

        function step(now) {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const value = Math.floor(current + (target - current) * progress);

            percentageEl.textContent = `${value}%`;

            if (progress < 1) {
                percentageRafId = requestAnimationFrame(step);
            } else {
                percentageEl.textContent = `${target}%`;
                percentageRafId = null;
            }
        }

        percentageRafId = requestAnimationFrame(step);
    }
}

export function resetPageLoaderProgress() {
    const loaderBar = document.querySelector('.page-loader__progress >div');
    const percentageEl = document.querySelector('.page-loader__percentage');

    if (loaderBar) {
        loaderBar.style.width = '0%';
        // Remove transition temporarily to reset instantly
        loaderBar.style.transition = 'none';
        requestAnimationFrame(() => {
            loaderBar.style.transition = 'width 0.2s linear';
        });
    }

    if (percentageEl) {
        if (percentageRafId) {
            cancelAnimationFrame(percentageRafId);
            percentageRafId = null;
        }
        percentageEl.textContent = '0%';
    }
}

/**
 * Monitors media assets loading in the viewport.
 * @param {HTMLElement} container - The container to search for media (usually document or data.next.container)
 * @returns {Promise} - Resolves when "above the fold" media is effectively loaded/playable.
 */
export function monitorMediaLoading(container = document, debug = false) {
    return new Promise((resolve) => {
        // Defines "Above the fold" as viewport height * 1.5 to be safe
        const foldThreshold = window.innerHeight * 1.5;
        
        // Select all relevant media
        const allMedia = Array.from(container.querySelectorAll('img, video'));
        
        // Filter for media reasonably near the viewport
        const visibleMedia = allMedia.filter(el => {
            // Skip media that can't reliably fire load/canplay — otherwise the
            // reveal stalls until the 3s safety timeout:
            //  - <img src=""> / no source: an empty src resolves to the page URL
            //    and never completes (e.g. the persistent image-zoom placeholder).
            //  - loading="lazy": the browser may never fetch it while off-screen.
            if (el.tagName.toLowerCase() === 'img') {
                if (!el.getAttribute('src') && !el.getAttribute('srcset')) return false;
                if (el.getAttribute('loading') === 'lazy') return false;
            } else if (el.tagName.toLowerCase() === 'video') {
                if (!el.getAttribute('src') && !el.querySelector('source')) return false;
            }

            // Skip media inside an inert/hidden subtree: closed dialogs (e.g. the
            // image-zoom overlay), off-canvas menus, hidden tab panels. We test
            // ANCESTORS via closest() rather than the element's own aria-hidden —
            // decorative-but-visible images (heroes, background media) are commonly
            // marked aria-hidden, so keying off the element itself would wrongly
            // skip real above-the-fold images. Note: we intentionally do NOT skip
            // on opacity:0 — .media content images start opacity:0 and fade in via
            // .is-inview, and the reveal should still wait for them to download.
            if (el.closest('[aria-hidden="true"], [hidden], [inert]')) return false;

            const rect = el.getBoundingClientRect();
             // Check if the element is within the fold threshold and not hidden
             // Note: getBoundingClientRect is relative to viewport.
             // If container is not in document yet, this might fail.
             // However, Barba 'enter' puts it in DOM.
             return (
                 rect.top < foldThreshold &&
                 rect.bottom > 0 &&
                 getComputedStyle(el).display !== 'none' &&
                 getComputedStyle(el).visibility !== 'hidden'
             );
        });

        if (visibleMedia.length === 0) {
            updatePageLoaderProgress(100);
            return resolve();
        }

        let completed = 0;
        const total = visibleMedia.length;
        
        // Determine initial progress based on already loaded items
        // We start visible progress at 10% just to show something happened
        let currentProgress = 10; 
        updatePageLoaderProgress(currentProgress);

        const checkProgress = () => {
            const ratio = completed / total;
            // Map 0-1 to 10-100%
            currentProgress = 10 + (ratio * 90);
            updatePageLoaderProgress(currentProgress);

            if (completed >= total) {
                resolve();
            }
        };

        visibleMedia.forEach(media => {
            if (media.tagName.toLowerCase() === 'img') {
                if (media.complete && media.naturalHeight > 0) {
                    completed++;
                    checkProgress();
                } else {
                    const onLoad = () => {
                        completed++;
                        checkProgress();
                        media.removeEventListener('load', onLoad);
                        media.removeEventListener('error', onLoad);
                    };
                    media.addEventListener('load', onLoad);
                    media.addEventListener('error', onLoad);
                }
            } else if (media.tagName.toLowerCase() === 'video') {
                // For videos, we just want them to be ready to play
                if (media.readyState >= 3) { // HAVE_FUTURE_DATA
                    completed++;
                    checkProgress();
                } else {
                    const onCanPlay = () => {
                        completed++;
                        checkProgress();
                        media.removeEventListener('canplay', onCanPlay);
                        media.removeEventListener('error', onCanPlay);
                    };
                    // Fallback if metadata loads but canplay doesn't fire immediately
                    const onMeta = () => {
                        if (media.readyState >= 3) {
                            onCanPlay();
                        }
                    };
                    
                    media.addEventListener('canplay', onCanPlay);
                    media.addEventListener('loadeddata', onMeta); // fallback
                    media.addEventListener('error', onCanPlay);
                }
            }
        });

        // Safety timeout: calculate valid progress even if events hang
        // Resolves after 3 seconds max, regardless.
        setTimeout(() => {
            if (completed < total) {
                if(debug) console.warn('Page Loader: Timeout reached waiting for media.');
                updatePageLoaderProgress(100);
                resolve();
            }
        }, 3000);
    });
}
